import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Checkbox,
  Typography,
  message,
  Flex,
  Space,
  Tag,
  Popconfirm,
  Collapse,
  Select,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const CUSTOMER_ALLOWED_MODULES = [
  'Dashboard', 'Engagements', 'Projects', 'Assessments', 'Jobs', 
  'Talent Management', 'Submitted Profiles', 'Hired Talents', 
  'Timesheet', 'Finance', 'Issues & Feedback', 'Team Management', 'Roles & Permissions'
];

const PARTNER_ALLOWED_MODULES = [
  'Dashboard', 'Ongoing Jobs', 'Bench Pool', 'Talent Management', 
  'Hired Talents', 'Timesheet', 'Finance', 'Issues & Feedback', 
  'Team Management', 'Roles & Permissions'
];

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const ProfileRolesWrapper = styled.div``;

const ProfileRoles = ({ parentRole = "Customer" }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [profileRoles, setProfileRoles] = useState([]);
  const [permissionModules, setPermissionModules] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isProfileRoleModalVisible, setIsProfileRoleModalVisible] = useState(false);
  const [editingProfileRole, setEditingProfileRole] = useState(null);

  const [profileRoleForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfileRoles();
    fetchPermissionsForSelection();
  }, []);

  const fetchPermissionsForSelection = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PERMISSIONS,
        { parentRole, isActive: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const allowedModules = parentRole === "Partner" 
          ? PARTNER_ALLOWED_MODULES 
          : CUSTOMER_ALLOWED_MODULES;
        
        const filteredPermissions = response.data.permissions.filter(
          (p) => allowedModules.includes(p.module)
        );
        
        setPermissions(filteredPermissions);
        const modules = [...new Set(filteredPermissions.map((p) => p.module))];
        setPermissionModules(modules);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const fetchProfileRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PROFILE_ROLES,
        {
          parentRole,
          includeGlobal: true,
          search: searchText || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setProfileRoles(response.data.profileRoles);
      }
    } catch (error) {
      console.error("Error fetching profile roles:", error);
      message.error("Failed to fetch profile roles");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProfileRoles();
  };

  const handleOpenProfileRoleModal = (record = null) => {
    setEditingProfileRole(record);
    if (record) {
      profileRoleForm.setFieldsValue({
        ...record,
        selectedPermissions: record.permissions || [],
      });
    } else {
      profileRoleForm.resetFields();
      profileRoleForm.setFieldValue("scope", "organization");
      profileRoleForm.setFieldValue("parentRole", parentRole);
    }
    setIsProfileRoleModalVisible(true);
  };

  const handleCloseProfileRoleModal = () => {
    setIsProfileRoleModalVisible(false);
    setEditingProfileRole(null);
    profileRoleForm.resetFields();
  };

  const handleProfileRoleSubmit = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        name: values.name,
        description: values.description,
        parentRole,
        scope: "organization",
        permissions: values.selectedPermissions || [],
        isDefault: values.isDefault || false,
      };

      if (editingProfileRole) {
        await axios.put(`${API_CONST.UPDATE_PROFILE_ROLE}/${editingProfileRole.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Profile role updated successfully");
      } else {
        await axios.post(API_CONST.CREATE_PROFILE_ROLE, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Profile role created successfully");
      }
      handleCloseProfileRoleModal();
      fetchProfileRoles();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to save profile role");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfileRole = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API_CONST.DELETE_PROFILE_ROLE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Profile role deleted");
      fetchProfileRoles();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to delete");
    }
  };

  const getPermissionsByModule = () => {
    const grouped = {};
    permissions.forEach((p) => {
      if (!grouped[p.module]) {
        grouped[p.module] = [];
      }
      grouped[p.module].push(p);
    });
    return grouped;
  };

  const profileRoleColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.isDefault && (
            <Tag color="gold" style={{ fontSize: 10 }}>
              DEFAULT
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      width: 120,
      render: (perms) => <Tag>{perms?.length || 0} permissions</Tag>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (active) =>
        active ? (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => {
        const isGlobal = record.scope === "global" || record.isDefault;
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              disabled={isGlobal}
              title={isGlobal ? "Default roles cannot be edited" : "Edit"}
              onClick={() => handleOpenProfileRoleModal(record)}
            />
            <Popconfirm
              title="Delete this profile role?"
              onConfirm={() => handleDeleteProfileRole(record.id)}
              disabled={isGlobal}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isGlobal}
                title={isGlobal ? "Default roles cannot be deleted" : "Delete"}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <ProfileRolesWrapper>
      <PageHeader>
        <h1>Role Management</h1>
        <p>Create and manage custom roles with specific permissions for your team members.</p>
      </PageHeader>

      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Space>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search profile roles"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 250 }}
          />
          <Button onClick={handleSearch}>Search</Button>
        </Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenProfileRoleModal()}
          style={{
            backgroundColor: "#00d9a9",
            borderColor: "#00d9a9",
          }}
        >
          Add Profile Role
        </Button>
      </Flex>

      <Table
        columns={profileRoleColumns}
        dataSource={profileRoles}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
      />

      <Modal
        title={editingProfileRole ? "Edit Profile Role" : "Add New Profile Role"}
        open={isProfileRoleModalVisible}
        onCancel={handleCloseProfileRoleModal}
        footer={null}
        width={700}
      >
        <Form form={profileRoleForm} layout="vertical" onFinish={handleProfileRoleSubmit}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a name" }]}>
            <Input placeholder="e.g., Team Lead, Reviewer" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Describe this profile role" rows={2} />
          </Form.Item>

          <Form.Item label="Permissions" name="selectedPermissions">
            <Collapse accordion>
              {Object.entries(getPermissionsByModule()).map(([module, perms]) => (
                <Panel header={<Text strong>{module}</Text>} key={module}>
                  <Checkbox.Group
                    options={perms.map((p) => ({
                      label: (
                        <span>
                          {p.name} <Text type="secondary">({p.code})</Text>
                        </span>
                      ),
                      value: p.code,
                    }))}
                    value={profileRoleForm.getFieldValue("selectedPermissions") || []}
                    onChange={(values) => {
                      const currentSelected = profileRoleForm.getFieldValue("selectedPermissions") || [];
                      const otherModulePerms = currentSelected.filter(
                        (code) => !perms.some((p) => p.code === code)
                      );
                      profileRoleForm.setFieldValue("selectedPermissions", [...otherModulePerms, ...values]);
                    }}
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  />
                </Panel>
              ))}
            </Collapse>
          </Form.Item>

          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox>Set as default role for new team members</Checkbox>
          </Form.Item>

          <Flex justify="flex-end" gap={12}>
            <Button onClick={handleCloseProfileRoleModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{ backgroundColor: "#00d9a9", borderColor: "#00d9a9" }}
            >
              {editingProfileRole ? "Update" : "Create"}
            </Button>
          </Flex>
        </Form>
      </Modal>
    </ProfileRolesWrapper>
  );
};

export default ProfileRoles;
